import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { toast } from 'sonner';
import axios from 'axios';
import { saveAs } from 'file-saver';
import { DropboxConnectCard } from '@/components/DropboxConnectCard';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { 
  LayoutDashboard, FileText, Settings, LogOut, 
  Download, Eye, Loader2, Clock, CheckCircle, 
  AlertCircle, RefreshCw, Globe, Trash2, Cloud,
  List, Plus, Edit2, Save, X, ChevronDown, ChevronUp,
  Shield, Lock, Eye as EyeIcon, EyeOff, Key, Mail, Copy, Send, ToggleLeft, ToggleRight,
  Menu
} from 'lucide-react';
import { Input } from '@/components/ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { language, toggleLanguage, t } = useLanguage();

  // Authenticated API helper
  const getAuthHeaders = () => {
    const token = localStorage.getItem('adminToken');
    return { Authorization: `Bearer ${token}` };
  };

  const authApi = {
    get: (url, config = {}) => axios.get(url, { ...config, headers: { ...config.headers, ...getAuthHeaders() } }),
    post: (url, data, config = {}) => axios.post(url, data, { ...config, headers: { ...config.headers, ...getAuthHeaders() } }),
    put: (url, data, config = {}) => axios.put(url, data, { ...config, headers: { ...config.headers, ...getAuthHeaders() } }),
    delete: (url, config = {}) => axios.delete(url, { ...config, headers: { ...config.headers, ...getAuthHeaders() } }),
  };

  // Handle 401 responses (token expired/invalid)
  axios.interceptors.response.use(
    response => response,
    error => {
      if (error.response?.status === 401 && window.location.pathname.includes('/admin/dashboard')) {
        localStorage.removeItem('adminToken');
        navigate('/admin');
        toast.error(language === 'it' ? 'Sessione scaduta. Effettua nuovamente il login.' : 'Session expired. Please login again.');
      }
      return Promise.reject(error);
    }
  );

  const [activeTab, setActiveTab] = useState('dashboard');
  const [stats, setStats] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [submissionToDelete, setSubmissionToDelete] = useState(null);
  
  // Questions management state
  const [selectedTypeForQuestions, setSelectedTypeForQuestions] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [loadingQuestions, setLoadingQuestions] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [newQuestion, setNewQuestion] = useState({ question_it: '', question_en: '' });
  const [showAddForm, setShowAddForm] = useState(false);
  
  // Password change state
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwordStatus, setPasswordStatus] = useState(null);

  // Access codes state
  const [accessCodes, setAccessCodes] = useState([]);
  const [loadingCodes, setLoadingCodes] = useState(false);
  const [emergencyCode, setEmergencyCode] = useState({ code: '', enabled: true });
  const [emailCodeForm, setEmailCodeForm] = useState({ email: '', client_name: '', expires_in_days: 7, language: 'it' });
  const [manualCodeForm, setManualCodeForm] = useState({ client_name: '', expires_in_days: 7 });
  const [generatingCode, setGeneratingCode] = useState(false);
  const [savingEmergency, setSavingEmergency] = useState(false);
  const [generatedCode, setGeneratedCode] = useState(null);

  // App settings state
  const [appSettings, setAppSettings] = useState({ app_url: '', sender_email: '', notification_email: '' });
  const [savingAppSettings, setSavingAppSettings] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin');
      return;
    }
    fetchData();
    fetchPasswordStatus();
  }, [navigate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, submissionsRes, typesRes] = await Promise.all([
        authApi.get(`${API}/stats`),
        authApi.get(`${API}/submissions`),
        authApi.get(`${API}/investigation-types?active_only=false`)
      ]);
      setStats(statsRes.data);
      setSubmissions(submissionsRes.data);
      setTypes(typesRes.data);
    } catch (error) {
      console.error(error);
      toast.error(t('error'));
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    navigate('/admin');
  };

  // Questions management functions
  const fetchQuestions = async (typeId) => {
    setLoadingQuestions(true);
    try {
      const response = await authApi.get(`${API}/investigation-types/${typeId}/questions`);
      setQuestions(response.data.questions);
      setSelectedTypeForQuestions(types.find(t => t.id === typeId));
    } catch (error) {
      console.error(error);
      toast.error(language === 'it' ? 'Errore caricamento domande' : 'Error loading questions');
    } finally {
      setLoadingQuestions(false);
    }
  };

  const fetchPasswordStatus = async () => {
    try {
      const response = await authApi.get(`${API}/admin/password-status`);
      setPasswordStatus(response.data);
    } catch (error) {
      console.error(error);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (!passwordData.current_password || !passwordData.new_password || !passwordData.confirm_password) {
      toast.error(language === 'it' ? 'Compila tutti i campi' : 'Fill all fields');
      return;
    }
    
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error(language === 'it' ? 'Le nuove password non coincidono' : 'New passwords do not match');
      return;
    }
    
    if (passwordData.new_password.length < 8) {
      toast.error(language === 'it' ? 'La password deve essere di almeno 8 caratteri' : 'Password must be at least 8 characters');
      return;
    }
    
    setChangingPassword(true);
    try {
      const response = await authApi.post(`${API}/admin/change-password`, passwordData);
      if (response.data.success) {
        toast.success(language === 'it' ? 'Password aggiornata con successo!' : 'Password updated successfully!');
        setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
        fetchPasswordStatus();
      }
    } catch (error) {
      console.error(error);
      const errorMessage = error.response?.data?.detail || (language === 'it' ? 'Errore cambio password' : 'Error changing password');
      toast.error(errorMessage);
    } finally {
      setChangingPassword(false);
    }
  };

  // Access Codes Functions
  const fetchAccessCodes = async () => {
    setLoadingCodes(true);
    try {
      const [codesRes, emergencyRes, settingsRes] = await Promise.all([
        authApi.get(`${API}/admin/access-codes?include_used=true`),
        authApi.get(`${API}/admin/emergency-code`),
        authApi.get(`${API}/admin/app-settings`)
      ]);
      setAccessCodes(codesRes.data);
      setEmergencyCode(emergencyRes.data);
      setAppSettings({
        app_url: settingsRes.data.app_url || '',
        sender_email: settingsRes.data.sender_email || '',
        notification_email: settingsRes.data.notification_email || ''
      });
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingCodes(false);
    }
  };

  const handleSaveAppSettings = async (e) => {
    e.preventDefault();
    if (!appSettings.app_url) {
      toast.error(language === 'it' ? 'Inserisci l\'URL dell\'app' : 'Enter app URL');
      return;
    }
    
    setSavingAppSettings(true);
    try {
      await authApi.put(`${API}/admin/app-settings`, appSettings);
      toast.success(language === 'it' ? 'Impostazioni salvate!' : 'Settings saved!');
    } catch (error) {
      console.error(error);
      toast.error(language === 'it' ? 'Errore salvataggio' : 'Error saving');
    } finally {
      setSavingAppSettings(false);
    }
  };

  const handleGenerateEmailCode = async (e) => {
    e.preventDefault();
    if (!emailCodeForm.email) {
      toast.error(language === 'it' ? 'Inserisci l\'email' : 'Enter email');
      return;
    }
    
    setGeneratingCode(true);
    try {
      const response = await authApi.post(`${API}/admin/access-codes/generate-email`, emailCodeForm);
      setGeneratedCode(response.data);
      if (response.data.email_sent) {
        toast.success(language === 'it' ? `Codice inviato a ${emailCodeForm.email}` : `Code sent to ${emailCodeForm.email}`);
      } else {
        toast.warning(language === 'it' ? 'Codice generato ma email non inviata. Comunicalo manualmente.' : 'Code generated but email not sent. Share it manually.');
      }
      setEmailCodeForm({ email: '', client_name: '', expires_in_days: 7, language: 'it' });
      fetchAccessCodes();
    } catch (error) {
      console.error(error);
      toast.error(language === 'it' ? 'Errore generazione codice' : 'Error generating code');
    } finally {
      setGeneratingCode(false);
    }
  };

  const handleGenerateManualCode = async (e) => {
    e.preventDefault();
    setGeneratingCode(true);
    try {
      const response = await authApi.post(`${API}/admin/access-codes/generate-manual`, manualCodeForm);
      setGeneratedCode(response.data);
      toast.success(language === 'it' ? 'Codice generato!' : 'Code generated!');
      setManualCodeForm({ client_name: '', expires_in_days: 7 });
      fetchAccessCodes();
    } catch (error) {
      console.error(error);
      toast.error(language === 'it' ? 'Errore generazione codice' : 'Error generating code');
    } finally {
      setGeneratingCode(false);
    }
  };

  const handleUpdateEmergencyCode = async (e) => {
    e.preventDefault();
    if (emergencyCode.code.length < 4) {
      toast.error(language === 'it' ? 'Il codice deve essere di almeno 4 caratteri' : 'Code must be at least 4 characters');
      return;
    }
    
    setSavingEmergency(true);
    try {
      await authApi.put(`${API}/admin/emergency-code`, emergencyCode);
      const status = emergencyCode.enabled ? 'attivato' : 'disattivato';
      toast.success(language === 'it' ? `Codice emergenza ${status}` : `Emergency code ${emergencyCode.enabled ? 'enabled' : 'disabled'}`);
    } catch (error) {
      console.error(error);
      toast.error(language === 'it' ? 'Errore salvataggio' : 'Error saving');
    } finally {
      setSavingEmergency(false);
    }
  };

  const handleDeleteCode = async (codeId) => {
    try {
      await authApi.delete(`${API}/admin/access-codes/${codeId}`);
      toast.success(language === 'it' ? 'Codice eliminato' : 'Code deleted');
      fetchAccessCodes();
    } catch (error) {
      console.error(error);
      toast.error(language === 'it' ? 'Errore eliminazione' : 'Error deleting');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success(language === 'it' ? 'Copiato!' : 'Copied!');
  };

  const handleAddQuestion = async () => {
    if (!newQuestion.question_it.trim() || !newQuestion.question_en.trim()) {
      toast.error(language === 'it' ? 'Compila entrambi i campi' : 'Fill both fields');
      return;
    }
    
    try {
      await authApi.post(`${API}/investigation-types/${selectedTypeForQuestions.id}/questions`, newQuestion);
      toast.success(language === 'it' ? 'Domanda aggiunta!' : 'Question added!');
      setNewQuestion({ question_it: '', question_en: '' });
      setShowAddForm(false);
      fetchQuestions(selectedTypeForQuestions.id);
    } catch (error) {
      console.error(error);
      toast.error(language === 'it' ? 'Errore aggiunta domanda' : 'Error adding question');
    }
  };

  const handleUpdateQuestion = async (index) => {
    if (!editingQuestion.question_it.trim() || !editingQuestion.question_en.trim()) {
      toast.error(language === 'it' ? 'Compila entrambi i campi' : 'Fill both fields');
      return;
    }
    
    try {
      await authApi.put(`${API}/investigation-types/${selectedTypeForQuestions.id}/questions/${index}`, {
        question_it: editingQuestion.question_it,
        question_en: editingQuestion.question_en
      });
      toast.success(language === 'it' ? 'Domanda aggiornata!' : 'Question updated!');
      setEditingQuestion(null);
      fetchQuestions(selectedTypeForQuestions.id);
    } catch (error) {
      console.error(error);
      toast.error(language === 'it' ? 'Errore aggiornamento' : 'Error updating');
    }
  };

  const handleDeleteQuestion = async (index) => {
    try {
      await authApi.delete(`${API}/investigation-types/${selectedTypeForQuestions.id}/questions/${index}`);
      toast.success(language === 'it' ? 'Domanda eliminata!' : 'Question deleted!');
      fetchQuestions(selectedTypeForQuestions.id);
    } catch (error) {
      console.error(error);
      toast.error(language === 'it' ? 'Errore eliminazione' : 'Error deleting');
    }
  };

  const handleDownloadPdf = async (submissionId) => {
    try {
      toast.info(language === 'it' ? 'Generazione PDF...' : 'Generating PDF...');
      
      const pdfUrl = `${API}/submissions/${submissionId}/pdf`;
      
      // Use fetch to get the PDF blob
      const response = await fetch(pdfUrl, { headers: getAuthHeaders() });
      
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      
      // Extract filename from Content-Disposition header
      const disposition = response.headers.get('content-disposition');
      let filename = `Dossier_${submissionId.slice(0, 8)}.pdf`;
      if (disposition) {
        const match = disposition.match(/filename[^;=\n]*=["']?([^";\n]*)["']?/);
        if (match && match[1]) {
          filename = match[1];
        }
      }
      
      const blob = await response.blob();
      saveAs(blob, filename);
      
      toast.success(language === 'it' ? 'PDF scaricato!' : 'PDF downloaded!');
    } catch (error) {
      console.error('PDF download error:', error);
      toast.error(language === 'it' ? 'Errore download PDF' : 'PDF download error');
    }
  };

  const handleUploadDropbox = async (submissionId) => {
    try {
      toast.info(language === 'it' ? 'Caricamento su Dropbox...' : 'Uploading to Dropbox...');
      
      const response = await authApi.post(`${API}/submissions/${submissionId}/upload-dropbox`);
      
      if (response.data.success) {
        toast.success(
          language === 'it' 
            ? `PDF caricato su Dropbox: ${response.data.filename}` 
            : `PDF uploaded to Dropbox: ${response.data.filename}`
        );
        fetchData(); // Refresh to show updated dropbox_path
      }
    } catch (error) {
      console.error('Dropbox upload error:', error);
      toast.error(
        language === 'it' 
          ? 'Errore caricamento Dropbox' 
          : 'Dropbox upload error'
      );
    }
  };

  const handleStatusChange = async (submissionId, newStatus) => {
    try {
      await authApi.put(`${API}/submissions/${submissionId}/status?status=${newStatus}`);
      fetchData();
      toast.success('Stato aggiornato');
    } catch (error) {
      console.error(error);
      toast.error(t('error'));
    }
  };

  const handleDeleteClick = (submission) => {
    setSubmissionToDelete(submission);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!submissionToDelete) return;
    
    try {
      await authApi.delete(`${API}/submissions/${submissionToDelete.id}`);
      fetchData();
      toast.success(t('deleted'));
    } catch (error) {
      console.error(error);
      toast.error(t('error'));
    } finally {
      setDeleteDialogOpen(false);
      setSubmissionToDelete(null);
    }
  };

  const viewDetails = (submission) => {
    setSelectedSubmission(submission);
    setDetailsOpen(true);
  };

  const closeDetails = () => {
    setDetailsOpen(false);
    setSelectedSubmission(null);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return <Clock className="w-4 h-4 text-yellow-500" />;
      case 'in_progress': return <AlertCircle className="w-4 h-4 text-neon-cyan" />;
      case 'completed': return <CheckCircle className="w-4 h-4 text-neon-green" />;
      default: return <Clock className="w-4 h-4 text-gray-500" />;
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'pending': return t('pending');
      case 'in_progress': return t('inProgress');
      case 'completed': return t('completed');
      default: return status;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'text-yellow-500';
      case 'in_progress': return 'text-neon-cyan';
      case 'completed': return 'text-neon-green';
      default: return 'text-gray-500';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-cyber-black flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-neon-green animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cyber-black">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-cyber-gray border-b border-white/10 z-50 flex items-center justify-between px-4"
           style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="p-2 text-gray-400 hover:text-white"
        >
          <Menu className="w-6 h-6" />
        </button>
        <h1 className="text-lg font-bold" style={{ fontFamily: "'Inter', sans-serif", color: '#E53935' }}>
          smart info
        </h1>
        <button
          onClick={handleLogout}
          className="p-2 text-neon-red"
        >
          <LogOut className="w-5 h-5" />
        </button>
      </div>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`fixed left-0 top-0 h-full w-64 bg-cyber-gray border-r border-white/10 z-50 transform transition-transform duration-300 lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}
             style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
        <div className="p-6">
          <h1 
            className="text-2xl font-bold tracking-tight"
            style={{ 
              fontFamily: "'Inter', sans-serif",
              color: '#E53935'
            }}
          >
            smart info
          </h1>
          <p className="text-xs text-gray-500 tracking-wider mt-1">ADMIN PANEL</p>
        </div>

        <nav className="px-4 space-y-2">
          <button
            onClick={() => { setActiveTab('dashboard'); setSidebarOpen(false); }}
            data-testid="nav-dashboard"
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg font-mono text-sm transition-colors ${
              activeTab === 'dashboard' 
                ? 'bg-neon-green/10 text-neon-green' 
                : 'text-gray-400 hover:text-white hover:bg-white/5'
            }`}
          >
            <LayoutDashboard className="w-5 h-5" />
            {t('dashboard')}
          </button>

          <button
            onClick={() => { setActiveTab('submissions'); setSidebarOpen(false); }}
            data-testid="nav-submissions"
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg font-mono text-sm transition-colors ${
              activeTab === 'submissions' 
                ? 'bg-neon-green/10 text-neon-green' 
                : 'text-gray-400 hover:text-white hover:bg-white/5'
            }`}
          >
            <FileText className="w-5 h-5" />
            {t('submissions')}
          </button>

          <button
            onClick={() => { setActiveTab('types'); setSidebarOpen(false); }}
            data-testid="nav-types"
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg font-mono text-sm transition-colors ${
              activeTab === 'types' 
                ? 'bg-neon-green/10 text-neon-green' 
                : 'text-gray-400 hover:text-white hover:bg-white/5'
            }`}
          >
            <Settings className="w-5 h-5" />
            {t('investigationTypes')}
          </button>

          <button
            onClick={() => { setActiveTab('questions'); setSidebarOpen(false); }}
            data-testid="nav-questions"
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg font-mono text-sm transition-colors ${
              activeTab === 'questions' 
                ? 'bg-neon-green/10 text-neon-green' 
                : 'text-gray-400 hover:text-white hover:bg-white/5'
            }`}
          >
            <List className="w-5 h-5" />
            {language === 'it' ? 'Domande Base' : 'Base Questions'}
          </button>

          <button
            onClick={() => { setActiveTab('access-codes'); fetchAccessCodes(); setSidebarOpen(false); }}
            data-testid="nav-access-codes"
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg font-mono text-sm transition-colors ${
              activeTab === 'access-codes' 
                ? 'bg-neon-green/10 text-neon-green' 
                : 'text-gray-400 hover:text-white hover:bg-white/5'
            }`}
          >
            <Key className="w-5 h-5" />
            {language === 'it' ? 'Codici Accesso' : 'Access Codes'}
          </button>

          <button
            onClick={() => { setActiveTab('email-settings'); fetchAccessCodes(); setSidebarOpen(false); }}
            data-testid="nav-email-settings"
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg font-mono text-sm transition-colors ${
              activeTab === 'email-settings' 
                ? 'bg-neon-green/10 text-neon-green' 
                : 'text-gray-400 hover:text-white hover:bg-white/5'
            }`}
          >
            <Mail className="w-5 h-5" />
            {language === 'it' ? 'Impostazioni Email' : 'Email Settings'}
          </button>

          <button
            onClick={() => { setActiveTab('security'); setSidebarOpen(false); }}
            data-testid="nav-security"
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg font-mono text-sm transition-colors ${
              activeTab === 'security' 
                ? 'bg-neon-green/10 text-neon-green' 
                : 'text-gray-400 hover:text-white hover:bg-white/5'
            }`}
          >
            <Shield className="w-5 h-5" />
            {language === 'it' ? 'Sicurezza' : 'Security'}
          </button>
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-white/10">
          <button
            onClick={toggleLanguage}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-lg font-mono text-sm text-gray-400 hover:text-white hover:bg-white/5 transition-colors mb-2"
          >
            <Globe className="w-5 h-5" />
            {language.toUpperCase()}
          </button>
          <button
            onClick={handleLogout}
            data-testid="logout-btn"
            className="w-full flex items-center gap-3 px-4 py-3 rounded-lg font-mono text-sm text-neon-red hover:bg-neon-red/10 transition-colors"
          >
            <LogOut className="w-5 h-5" />
            {t('logout')}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 p-4 lg:p-8 pt-20 lg:pt-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 lg:mb-8">
          <h2 className="font-orbitron text-xl lg:text-2xl text-white">
            {activeTab === 'dashboard' && t('dashboard')}
            {activeTab === 'submissions' && t('submissions')}
            {activeTab === 'types' && t('investigationTypes')}
            {activeTab === 'questions' && (language === 'it' ? 'Gestione Domande Base' : 'Base Questions Management')}
            {activeTab === 'access-codes' && (language === 'it' ? 'Gestione Codici Accesso' : 'Access Codes Management')}
            {activeTab === 'email-settings' && (language === 'it' ? 'Impostazioni Email' : 'Email Settings')}
            {activeTab === 'security' && (language === 'it' ? 'Sicurezza' : 'Security')}
          </h2>
          <button
            onClick={fetchData}
            className="cyber-btn-outline px-4 py-2 flex items-center gap-2 rounded-md self-start sm:self-auto"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>

        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && stats && (
          <div className="space-y-8">
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="cyber-card rounded-lg p-6">
                <div className="font-mono text-xs text-gray-500 mb-2">{t('total')}</div>
                <div className="font-orbitron text-3xl text-white">{stats.total}</div>
              </div>
              <div className="cyber-card rounded-lg p-6">
                <div className="font-mono text-xs text-yellow-500 mb-2">{t('pending')}</div>
                <div className="font-orbitron text-3xl text-yellow-500">{stats.pending}</div>
              </div>
              <div className="cyber-card rounded-lg p-6">
                <div className="font-mono text-xs text-neon-cyan mb-2">{t('inProgress')}</div>
                <div className="font-orbitron text-3xl text-neon-cyan">{stats.in_progress}</div>
              </div>
              <div className="cyber-card rounded-lg p-6">
                <div className="font-mono text-xs text-neon-green mb-2">{t('completed')}</div>
                <div className="font-orbitron text-3xl text-neon-green">{stats.completed}</div>
              </div>
            </div>

            {/* By Type */}
            <div className="cyber-card rounded-lg p-6">
              <h3 className="font-orbitron text-lg text-white mb-4">{t('investigationTypes')}</h3>
              <div className="space-y-3">
                {Object.entries(stats.by_type || {}).map(([type, count]) => (
                  <div key={type} className="flex items-center justify-between">
                    <span className="font-mono text-sm text-gray-300">{type}</span>
                    <span className="font-mono text-neon-green">{count}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Submissions Tab */}
        {activeTab === 'submissions' && (
          <div className="cyber-card rounded-lg overflow-hidden">
            {/* Mobile Cards View */}
            <div className="lg:hidden">
              <ScrollArea className="h-[calc(100vh-200px)]">
                <div className="p-4 space-y-4">
                  {submissions.length === 0 ? (
                    <p className="text-center py-12 text-gray-500 font-mono">
                      {t('noSubmissions')}
                    </p>
                  ) : (
                    submissions.map((sub) => (
                      <div key={sub.id} className="bg-cyber-surface rounded-lg p-4 space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="font-mono text-xs text-gray-500">#{sub.id.slice(0, 8)}</span>
                          <div className="flex items-center gap-2">
                            {getStatusIcon(sub.status)}
                            <span className={`font-mono text-xs ${getStatusColor(sub.status)}`}>
                              {getStatusLabel(sub.status)}
                            </span>
                          </div>
                        </div>
                        <p className="font-mono text-sm text-white">{sub.investigation_type_name}</p>
                        <p className="font-mono text-xs text-gray-400">{sub.created_at?.slice(0, 10)}</p>
                        <div className="flex gap-2 pt-2 border-t border-white/10">
                          <button
                            onClick={() => viewDetails(sub)}
                            className="flex-1 cyber-btn-outline px-3 py-2 text-xs rounded flex items-center justify-center gap-1"
                          >
                            <Eye className="w-3 h-3" /> {t('view')}
                          </button>
                          <button
                            onClick={() => handleDownloadPdf(sub.id)}
                            className="flex-1 cyber-btn px-3 py-2 text-xs rounded flex items-center justify-center gap-1"
                          >
                            <Download className="w-3 h-3" />
                            PDF
                          </button>
                          <button
                            onClick={() => handleDeleteClick(sub)}
                            className="px-3 py-2 text-neon-red hover:bg-neon-red/10 rounded"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </ScrollArea>
            </div>

            {/* Desktop Table View */}
            <div className="hidden lg:block">
              <ScrollArea className="h-[600px]">
                <table className="w-full">
                  <thead className="bg-cyber-surface sticky top-0">
                    <tr>
                      <th className="text-left px-6 py-4 font-mono text-xs text-neon-green uppercase">ID</th>
                      <th className="text-left px-6 py-4 font-mono text-xs text-neon-green uppercase">{t('type')}</th>
                      <th className="text-left px-6 py-4 font-mono text-xs text-neon-green uppercase">{t('date')}</th>
                      <th className="text-left px-6 py-4 font-mono text-xs text-neon-green uppercase">{t('status')}</th>
                      <th className="text-left px-6 py-4 font-mono text-xs text-neon-green uppercase">{t('actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {submissions.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="text-center py-12 text-gray-500 font-mono">
                          {t('noSubmissions')}
                        </td>
                      </tr>
                    ) : (
                      submissions.map((sub) => (
                        <tr key={sub.id} className="border-t border-white/5 hover:bg-white/5">
                          <td className="px-6 py-4 font-mono text-sm text-gray-300">
                            {sub.id.slice(0, 8)}...
                          </td>
                          <td className="px-6 py-4 font-mono text-sm text-white">
                            {sub.investigation_type_name}
                          </td>
                          <td className="px-6 py-4 font-mono text-sm text-gray-400">
                            {sub.created_at?.slice(0, 10)}
                          </td>
                          <td className="px-6 py-4">
                            <Select
                              value={sub.status}
                              onValueChange={(value) => handleStatusChange(sub.id, value)}
                            >
                              <SelectTrigger className="w-36 bg-transparent border-white/20 font-mono text-sm">
                                <SelectValue>
                                  <div className="flex items-center gap-2">
                                    {getStatusIcon(sub.status)}
                                  {getStatusLabel(sub.status)}
                                </div>
                              </SelectValue>
                            </SelectTrigger>
                            <SelectContent className="bg-cyber-gray border-white/20">
                              <SelectItem value="pending" className="font-mono">
                                <div className="flex items-center gap-2">
                                  <Clock className="w-4 h-4 text-yellow-500" />
                                  {t('pending')}
                                </div>
                              </SelectItem>
                              <SelectItem value="in_progress" className="font-mono">
                                <div className="flex items-center gap-2">
                                  <AlertCircle className="w-4 h-4 text-neon-cyan" />
                                  {t('inProgress')}
                                </div>
                              </SelectItem>
                              <SelectItem value="completed" className="font-mono">
                                <div className="flex items-center gap-2">
                                  <CheckCircle className="w-4 h-4 text-neon-green" />
                                  {t('completed')}
                                </div>
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => viewDetails(sub)}
                              data-testid={`view-details-${sub.id}`}
                              className="p-2 hover:bg-neon-cyan/10 rounded transition-colors"
                              title={t('viewDetails')}
                            >
                              <Eye className="w-4 h-4 text-neon-cyan" />
                            </button>
                            <button
                              onClick={() => handleDownloadPdf(sub.id)}
                              data-testid={`download-pdf-${sub.id}`}
                              className="p-2 hover:bg-neon-green/10 rounded transition-colors"
                              title={t('downloadPdf')}
                            >
                              <Download className="w-4 h-4 text-neon-green" />
                            </button>
                            <button
                              onClick={() => handleUploadDropbox(sub.id)}
                              data-testid={`upload-dropbox-${sub.id}`}
                              className={`p-2 hover:bg-blue-500/10 rounded transition-colors ${sub.dropbox_path ? 'text-blue-400' : 'text-blue-500'}`}
                              title={sub.dropbox_path 
                                ? (language === 'it' ? 'Già su Dropbox - Clicca per aggiornare' : 'Already on Dropbox - Click to update')
                                : (language === 'it' ? 'Carica su Dropbox' : 'Upload to Dropbox')
                              }
                            >
                              <Cloud className={`w-4 h-4 ${sub.dropbox_path ? 'fill-current' : ''}`} />
                            </button>
                            <button
                              onClick={() => handleDeleteClick(sub)}
                              data-testid={`delete-${sub.id}`}
                              className="p-2 hover:bg-neon-red/10 rounded transition-colors"
                              title={t('delete')}
                            >
                              <Trash2 className="w-4 h-4 text-neon-red" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </ScrollArea>
            </div>
          </div>
        )}

        {/* Types Tab */}
        {activeTab === 'types' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {types.map((type) => (
              <div key={type.id} className="cyber-card rounded-lg p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="font-orbitron text-lg text-white mb-1">
                      {type[`name_${language}`] || type.name_it}
                    </h3>
                    <p className="font-mono text-xs text-gray-500">
                      {type[`description_${language}`] || type.description_it}
                    </p>
                  </div>
                  <div className={`px-3 py-1 rounded font-mono text-xs ${
                    type.active ? 'bg-neon-green/20 text-neon-green' : 'bg-gray-500/20 text-gray-500'
                  }`}>
                    {type.active ? 'ACTIVE' : 'INACTIVE'}
                  </div>
                </div>
                <div className="font-mono text-xs text-gray-400">
                  {(type[`base_questions_${language}`] || type.base_questions_it)?.length || 0} domande base
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Questions Tab */}
        {activeTab === 'questions' && (
          <div className="space-y-6">
            {/* Type Selector */}
            {!selectedTypeForQuestions ? (
              <div className="cyber-card rounded-lg p-6">
                <h3 className="font-orbitron text-lg text-white mb-4">
                  {language === 'it' ? 'Seleziona Tipologia Indagine' : 'Select Investigation Type'}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {types.map((type) => (
                    <button
                      key={type.id}
                      onClick={() => fetchQuestions(type.id)}
                      className="cyber-card rounded-lg p-4 text-left hover:border-neon-green/50 transition-colors"
                    >
                      <h4 className="font-orbitron text-white mb-1">
                        {type[`name_${language}`] || type.name_it}
                      </h4>
                      <p className="font-mono text-xs text-gray-500">
                        {(type[`base_questions_${language}`] || type.base_questions_it)?.length || 0} {language === 'it' ? 'domande' : 'questions'}
                      </p>
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <>
                {/* Back button and Type info */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <button
                      onClick={() => {
                        setSelectedTypeForQuestions(null);
                        setQuestions([]);
                        setShowAddForm(false);
                        setEditingQuestion(null);
                      }}
                      className="cyber-btn-outline px-4 py-2 rounded-md text-sm"
                    >
                      ← {language === 'it' ? 'Indietro' : 'Back'}
                    </button>
                    <div>
                      <h3 className="font-orbitron text-lg text-white">
                        {selectedTypeForQuestions[`name_${language}`] || selectedTypeForQuestions.name_it}
                      </h3>
                      <p className="font-mono text-xs text-gray-500">
                        {questions.length} {language === 'it' ? 'domande base' : 'base questions'}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowAddForm(!showAddForm)}
                    className="cyber-btn px-4 py-2 flex items-center gap-2 rounded-md"
                  >
                    <Plus className="w-4 h-4" />
                    {language === 'it' ? 'Aggiungi Domanda' : 'Add Question'}
                  </button>
                </div>

                {/* Add Question Form */}
                {showAddForm && (
                  <div className="cyber-card rounded-lg p-6 border-neon-green/30">
                    <h4 className="font-orbitron text-white mb-4">
                      {language === 'it' ? 'Nuova Domanda' : 'New Question'}
                    </h4>
                    <div className="space-y-4">
                      <div>
                        <label className="font-mono text-xs text-gray-400 block mb-1">Italiano</label>
                        <Input
                          value={newQuestion.question_it}
                          onChange={(e) => setNewQuestion({...newQuestion, question_it: e.target.value})}
                          placeholder="Inserisci la domanda in italiano..."
                          className="cyber-input w-full"
                        />
                      </div>
                      <div>
                        <label className="font-mono text-xs text-gray-400 block mb-1">English</label>
                        <Input
                          value={newQuestion.question_en}
                          onChange={(e) => setNewQuestion({...newQuestion, question_en: e.target.value})}
                          placeholder="Enter the question in English..."
                          className="cyber-input w-full"
                        />
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={handleAddQuestion}
                          className="cyber-btn px-4 py-2 flex items-center gap-2 rounded-md"
                        >
                          <Save className="w-4 h-4" />
                          {language === 'it' ? 'Salva' : 'Save'}
                        </button>
                        <button
                          onClick={() => {
                            setShowAddForm(false);
                            setNewQuestion({ question_it: '', question_en: '' });
                          }}
                          className="cyber-btn-outline px-4 py-2 rounded-md"
                        >
                          {language === 'it' ? 'Annulla' : 'Cancel'}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Questions List */}
                {loadingQuestions ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 text-neon-green animate-spin" />
                  </div>
                ) : (
                  <div className="space-y-3">
                    {questions.map((q, idx) => (
                      <div key={idx} className="cyber-card rounded-lg p-4">
                        {editingQuestion?.index === idx ? (
                          // Edit mode
                          <div className="space-y-3">
                            <div>
                              <label className="font-mono text-xs text-gray-400 block mb-1">Italiano</label>
                              <Input
                                value={editingQuestion.question_it}
                                onChange={(e) => setEditingQuestion({...editingQuestion, question_it: e.target.value})}
                                className="cyber-input w-full"
                              />
                            </div>
                            <div>
                              <label className="font-mono text-xs text-gray-400 block mb-1">English</label>
                              <Input
                                value={editingQuestion.question_en}
                                onChange={(e) => setEditingQuestion({...editingQuestion, question_en: e.target.value})}
                                className="cyber-input w-full"
                              />
                            </div>
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleUpdateQuestion(idx)}
                                className="cyber-btn px-3 py-1.5 flex items-center gap-1 rounded text-sm"
                              >
                                <Save className="w-3 h-3" />
                                {language === 'it' ? 'Salva' : 'Save'}
                              </button>
                              <button
                                onClick={() => setEditingQuestion(null)}
                                className="cyber-btn-outline px-3 py-1.5 rounded text-sm"
                              >
                                {language === 'it' ? 'Annulla' : 'Cancel'}
                              </button>
                            </div>
                          </div>
                        ) : (
                          // View mode
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <span className="font-mono text-xs text-neon-green bg-neon-green/10 px-2 py-0.5 rounded">
                                  #{idx + 1}
                                </span>
                              </div>
                              <p className="text-white mb-1">{q.question_it}</p>
                              <p className="text-gray-500 text-sm">{q.question_en}</p>
                            </div>
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => setEditingQuestion({
                                  index: idx,
                                  question_it: q.question_it,
                                  question_en: q.question_en
                                })}
                                className="p-2 hover:bg-neon-cyan/10 rounded transition-colors"
                                title={language === 'it' ? 'Modifica' : 'Edit'}
                              >
                                <Edit2 className="w-4 h-4 text-neon-cyan" />
                              </button>
                              <button
                                onClick={() => handleDeleteQuestion(idx)}
                                className="p-2 hover:bg-neon-red/10 rounded transition-colors"
                                title={language === 'it' ? 'Elimina' : 'Delete'}
                              >
                                <Trash2 className="w-4 h-4 text-neon-red" />
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Note about AI questions */}
                <div className="cyber-card rounded-lg p-4 border-neon-cyan/20 bg-neon-cyan/5">
                  <p className="font-mono text-xs text-neon-cyan">
                    ℹ️ {language === 'it' 
                      ? 'Queste sono le domande base. Le domande di approfondimento vengono generate automaticamente dall\'AI durante il questionario.' 
                      : 'These are base questions. Follow-up questions are automatically generated by AI during the questionnaire.'}
                  </p>
                </div>
              </>
            )}
          </div>
        )}

        {/* Email Settings Tab */}
        {activeTab === 'email-settings' && (
          <div className="max-w-2xl space-y-6">
            {/* App URL Settings */}
            <div className="cyber-card rounded-lg p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 rounded-lg bg-neon-cyan/10">
                  <Globe className="w-6 h-6 text-neon-cyan" />
                </div>
                <div>
                  <h3 className="font-orbitron text-lg text-white">
                    {language === 'it' ? 'URL Applicazione' : 'Application URL'}
                  </h3>
                  <p className="font-mono text-xs text-gray-500">
                    {language === 'it' ? 'URL che apparirà nelle email ai clienti' : 'URL that will appear in client emails'}
                  </p>
                </div>
              </div>
              
              <form onSubmit={handleSaveAppSettings} className="space-y-4">
                <div>
                  <label className="font-mono text-xs text-gray-400 block mb-2">
                    {language === 'it' ? 'URL Applicazione *' : 'Application URL *'}
                  </label>
                  <Input
                    type="url"
                    value={appSettings.app_url}
                    onChange={(e) => setAppSettings({...appSettings, app_url: e.target.value})}
                    placeholder="https://tuodominio.it"
                    className="cyber-input w-full"
                    required
                  />
                </div>
                <div>
                  <label className="font-mono text-xs text-gray-400 block mb-2">
                    {language === 'it' ? 'Email Mittente' : 'Sender Email'}
                  </label>
                  <Input
                    type="email"
                    value={appSettings.sender_email}
                    onChange={(e) => setAppSettings({...appSettings, sender_email: e.target.value})}
                    placeholder="noreply@app.smart-info.it"
                    className="cyber-input w-full"
                  />
                  <p className="font-mono text-xs text-gray-600 mt-1">
                    {language === 'it' ? 'Deve essere un dominio verificato su Resend' : 'Must be a verified domain on Resend'}
                  </p>
                </div>
                <div>
                  <label className="font-mono text-xs text-gray-400 block mb-2">
                    {language === 'it' ? 'Email Notifiche Nuove Schede *' : 'New Submission Notification Email *'}
                  </label>
                  <Input
                    type="email"
                    value={appSettings.notification_email}
                    onChange={(e) => setAppSettings({...appSettings, notification_email: e.target.value})}
                    placeholder="admin@smart-info.it"
                    className="cyber-input w-full"
                    data-testid="notification-email-input"
                  />
                  <p className="font-mono text-xs text-gray-600 mt-1">
                    {language === 'it' 
                      ? 'Riceverai una notifica email ogni volta che un cliente completa una scheda. Puoi inserire piu email separate da virgola.' 
                      : 'You will receive a notification email every time a client completes a form. You can enter multiple emails separated by comma.'}
                  </p>
                </div>
                <button
                  type="submit"
                  disabled={savingAppSettings}
                  className="cyber-btn px-4 py-3 flex items-center gap-2 rounded-md"
                >
                  {savingAppSettings ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  {language === 'it' ? 'Salva Impostazioni' : 'Save Settings'}
                </button>
              </form>
            </div>

            {/* Email Preview Info */}
            <div className="cyber-card rounded-lg p-4 border-neon-cyan/20 bg-neon-cyan/5">
              <p className="font-mono text-xs text-neon-cyan">
                {language === 'it' 
                  ? 'Queste impostazioni vengono usate per le email di invio codice ai clienti e le notifiche nuove schede.' 
                  : 'These settings are used for sending access code emails to clients and new submission notifications.'}
              </p>
            </div>

            {/* Install Admin App */}
            <div className="cyber-card rounded-lg p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 rounded-lg bg-neon-red/10">
                  <Download className="w-6 h-6 text-neon-red" />
                </div>
                <div>
                  <h3 className="font-orbitron text-lg text-white">
                    {language === 'it' ? 'Installa App Admin' : 'Install Admin App'}
                  </h3>
                  <p className="font-mono text-xs text-gray-500">
                    {language === 'it' ? 'Installa Smart Admin come app sul tuo smartphone' : 'Install Smart Admin as an app on your phone'}
                  </p>
                </div>
              </div>
              <a 
                href="/admin-app.html"
                target="_blank"
                rel="noopener noreferrer"
                className="cyber-btn px-4 py-3 flex items-center justify-center gap-2 rounded-md w-full text-center"
                data-testid="install-admin-app-link"
              >
                <Download className="w-4 h-4" />
                {language === 'it' ? 'Apri Pagina di Installazione' : 'Open Install Page'}
              </a>
            </div>
            <DropboxConnectCard authApi={authApi} language={language} />
          </div>
        )}

        {/* Security Tab */}
        {activeTab === 'security' && (
          <div className="max-w-xl">
            <div className="cyber-card rounded-lg p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 rounded-lg bg-neon-green/10">
                  <Lock className="w-6 h-6 text-neon-green" />
                </div>
                <div>
                  <h3 className="font-orbitron text-lg text-white">
                    {language === 'it' ? 'Cambia Password' : 'Change Password'}
                  </h3>
                  <p className="font-mono text-xs text-gray-500">
                    {passwordStatus?.custom_password_set 
                      ? (language === 'it' 
                          ? `Ultima modifica: ${new Date(passwordStatus.last_updated).toLocaleDateString('it-IT')}` 
                          : `Last changed: ${new Date(passwordStatus.last_updated).toLocaleDateString('en-US')}`)
                      : (language === 'it' ? 'Password predefinita attiva' : 'Default password active')}
                  </p>
                </div>
              </div>

              <form onSubmit={handleChangePassword} className="space-y-4">
                {/* Current Password */}
                <div>
                  <label className="font-mono text-xs text-gray-400 block mb-2">
                    {language === 'it' ? 'Password Attuale' : 'Current Password'}
                  </label>
                  <div className="relative">
                    <Input
                      type={showCurrentPassword ? 'text' : 'password'}
                      value={passwordData.current_password}
                      onChange={(e) => setPasswordData({...passwordData, current_password: e.target.value})}
                      placeholder={language === 'it' ? 'Inserisci password attuale' : 'Enter current password'}
                      className="cyber-input w-full pr-10"
                      data-testid="current-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white transition-colors"
                    >
                      {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <EyeIcon className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                {/* New Password */}
                <div>
                  <label className="font-mono text-xs text-gray-400 block mb-2">
                    {language === 'it' ? 'Nuova Password' : 'New Password'}
                  </label>
                  <div className="relative">
                    <Input
                      type={showNewPassword ? 'text' : 'password'}
                      value={passwordData.new_password}
                      onChange={(e) => setPasswordData({...passwordData, new_password: e.target.value})}
                      placeholder={language === 'it' ? 'Inserisci nuova password (min. 8 caratteri)' : 'Enter new password (min. 8 characters)'}
                      className="cyber-input w-full pr-10"
                      data-testid="new-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white transition-colors"
                    >
                      {showNewPassword ? <EyeOff className="w-4 h-4" /> : <EyeIcon className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                {/* Confirm Password */}
                <div>
                  <label className="font-mono text-xs text-gray-400 block mb-2">
                    {language === 'it' ? 'Conferma Nuova Password' : 'Confirm New Password'}
                  </label>
                  <div className="relative">
                    <Input
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={passwordData.confirm_password}
                      onChange={(e) => setPasswordData({...passwordData, confirm_password: e.target.value})}
                      placeholder={language === 'it' ? 'Ripeti la nuova password' : 'Repeat new password'}
                      className="cyber-input w-full pr-10"
                      data-testid="confirm-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white transition-colors"
                    >
                      {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <EyeIcon className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                {/* Password requirements hint */}
                <div className="p-3 rounded-lg bg-cyber-surface/50 border border-white/5">
                  <p className="font-mono text-xs text-gray-500">
                    {language === 'it' 
                      ? '• La password deve essere di almeno 8 caratteri'
                      : '• Password must be at least 8 characters'}
                  </p>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={changingPassword}
                  data-testid="change-password-btn"
                  className="w-full cyber-btn px-4 py-3 flex items-center justify-center gap-2 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {changingPassword ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Lock className="w-4 h-4" />
                  )}
                  {language === 'it' ? 'Aggiorna Password' : 'Update Password'}
                </button>
              </form>
            </div>

            {/* Security Tips */}
            <div className="cyber-card rounded-lg p-4 mt-4 border-neon-cyan/20 bg-neon-cyan/5">
              <p className="font-mono text-xs text-neon-cyan">
                🔒 {language === 'it' 
                  ? 'Consiglio: usa una password unica e complessa. Non condividere mai le tue credenziali.' 
                  : 'Tip: Use a unique, complex password. Never share your credentials.'}
              </p>
            </div>
          </div>
        )}

        {/* Access Codes Tab */}
        {activeTab === 'access-codes' && (
          <div className="space-y-6">
            {/* Generated Code Display */}
            {generatedCode && (
              <div className="cyber-card rounded-lg p-6 border-neon-green/30 bg-neon-green/5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-mono text-xs text-gray-400 mb-2">
                      {language === 'it' ? 'Codice Generato' : 'Generated Code'}
                    </p>
                    <div className="flex items-center gap-4">
                      <span className="font-orbitron text-3xl text-neon-green tracking-[0.3em]">
                        {generatedCode.code}
                      </span>
                      <button
                        onClick={() => copyToClipboard(generatedCode.code)}
                        className="p-2 rounded bg-white/5 hover:bg-white/10 transition-colors"
                      >
                        <Copy className="w-5 h-5 text-gray-400" />
                      </button>
                    </div>
                    {generatedCode.email && (
                      <p className="font-mono text-xs text-gray-400 mt-2">
                        {generatedCode.email_sent 
                          ? `✓ ${language === 'it' ? 'Inviato a' : 'Sent to'}: ${generatedCode.email}`
                          : `⚠ ${language === 'it' ? 'Email non inviata - comunicalo manualmente' : 'Email not sent - share manually'}`
                        }
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => setGeneratedCode(null)}
                    className="text-gray-500 hover:text-white"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Generate Code via Email */}
              <div className="cyber-card rounded-lg p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 rounded-lg bg-neon-cyan/10">
                    <Mail className="w-5 h-5 text-neon-cyan" />
                  </div>
                  <h3 className="font-orbitron text-lg text-white">
                    {language === 'it' ? 'Invia Codice via Email' : 'Send Code via Email'}
                  </h3>
                </div>
                
                <form onSubmit={handleGenerateEmailCode} className="space-y-4">
                  <div>
                    <label className="font-mono text-xs text-gray-400 block mb-2">Email *</label>
                    <Input
                      type="email"
                      value={emailCodeForm.email}
                      onChange={(e) => setEmailCodeForm({...emailCodeForm, email: e.target.value})}
                      placeholder="cliente@email.com"
                      className="cyber-input w-full"
                      required
                    />
                  </div>
                  <div>
                    <label className="font-mono text-xs text-gray-400 block mb-2">
                      {language === 'it' ? 'Nome Cliente (opzionale)' : 'Client Name (optional)'}
                    </label>
                    <Input
                      type="text"
                      value={emailCodeForm.client_name}
                      onChange={(e) => setEmailCodeForm({...emailCodeForm, client_name: e.target.value})}
                      placeholder="Mario Rossi"
                      className="cyber-input w-full"
                    />
                  </div>
                  <div>
                    <label className="font-mono text-xs text-gray-400 block mb-2">
                      {language === 'it' ? 'Scadenza (giorni)' : 'Expires in (days)'}
                    </label>
                    <Input
                      type="number"
                      min="1"
                      max="30"
                      value={emailCodeForm.expires_in_days}
                      onChange={(e) => setEmailCodeForm({...emailCodeForm, expires_in_days: parseInt(e.target.value)})}
                      className="cyber-input w-full"
                    />
                  </div>
                  <div>
                    <label className="font-mono text-xs text-gray-400 block mb-2">
                      {language === 'it' ? 'Lingua Email' : 'Email Language'}
                    </label>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setEmailCodeForm({...emailCodeForm, language: 'it'})}
                        className={`flex-1 px-4 py-2 rounded-md font-mono text-sm transition-colors ${
                          emailCodeForm.language === 'it'
                            ? 'bg-neon-green/20 text-neon-green border border-neon-green/50'
                            : 'bg-white/5 text-gray-400 border border-white/10 hover:bg-white/10'
                        }`}
                      >
                        🇮🇹 Italiano
                      </button>
                      <button
                        type="button"
                        onClick={() => setEmailCodeForm({...emailCodeForm, language: 'en'})}
                        className={`flex-1 px-4 py-2 rounded-md font-mono text-sm transition-colors ${
                          emailCodeForm.language === 'en'
                            ? 'bg-neon-green/20 text-neon-green border border-neon-green/50'
                            : 'bg-white/5 text-gray-400 border border-white/10 hover:bg-white/10'
                        }`}
                      >
                        🇬🇧 English
                      </button>
                    </div>
                  </div>
                  <button
                    type="submit"
                    disabled={generatingCode}
                    className="w-full cyber-btn px-4 py-3 flex items-center justify-center gap-2 rounded-md"
                  >
                    {generatingCode ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    {language === 'it' ? 'Genera e Invia' : 'Generate & Send'}
                  </button>
                </form>
              </div>

              {/* Generate Manual Code */}
              <div className="cyber-card rounded-lg p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 rounded-lg bg-yellow-500/10">
                    <Key className="w-5 h-5 text-yellow-500" />
                  </div>
                  <h3 className="font-orbitron text-lg text-white">
                    {language === 'it' ? 'Genera Codice Manuale' : 'Generate Manual Code'}
                  </h3>
                </div>
                <p className="font-mono text-xs text-gray-500 mb-4">
                  {language === 'it' 
                    ? 'Genera un codice da comunicare manualmente al cliente (telefono, di persona, etc.)' 
                    : 'Generate a code to share manually with the client (phone, in person, etc.)'}
                </p>
                
                <form onSubmit={handleGenerateManualCode} className="space-y-4">
                  <div>
                    <label className="font-mono text-xs text-gray-400 block mb-2">
                      {language === 'it' ? 'Nome Cliente (opzionale)' : 'Client Name (optional)'}
                    </label>
                    <Input
                      type="text"
                      value={manualCodeForm.client_name}
                      onChange={(e) => setManualCodeForm({...manualCodeForm, client_name: e.target.value})}
                      placeholder="Mario Rossi"
                      className="cyber-input w-full"
                    />
                  </div>
                  <div>
                    <label className="font-mono text-xs text-gray-400 block mb-2">
                      {language === 'it' ? 'Scadenza (giorni)' : 'Expires in (days)'}
                    </label>
                    <Input
                      type="number"
                      min="1"
                      max="30"
                      value={manualCodeForm.expires_in_days}
                      onChange={(e) => setManualCodeForm({...manualCodeForm, expires_in_days: parseInt(e.target.value)})}
                      className="cyber-input w-full"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={generatingCode}
                    className="w-full cyber-btn-outline px-4 py-3 flex items-center justify-center gap-2 rounded-md"
                  >
                    {generatingCode ? <Loader2 className="w-4 h-4 animate-spin" /> : <Key className="w-4 h-4" />}
                    {language === 'it' ? 'Genera Codice' : 'Generate Code'}
                  </button>
                </form>
              </div>
            </div>

            {/* Emergency Code */}
            <div className="cyber-card rounded-lg p-6 border-red-500/30">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 rounded-lg bg-red-500/10">
                  <Shield className="w-5 h-5 text-red-500" />
                </div>
                <h3 className="font-orbitron text-lg text-white">
                  {language === 'it' ? 'Codice Emergenza' : 'Emergency Code'}
                </h3>
              </div>
              <p className="font-mono text-xs text-gray-500 mb-4">
                {language === 'it' 
                  ? 'Codice universale sempre valido. Usalo per emergenze o test. Può essere disattivato.' 
                  : 'Universal code always valid. Use for emergencies or testing. Can be disabled.'}
              </p>
              
              <form onSubmit={handleUpdateEmergencyCode} className="space-y-4">
                <div className="flex gap-4">
                  <div className="flex-1">
                    <label className="font-mono text-xs text-gray-400 block mb-2">
                      {language === 'it' ? 'Codice' : 'Code'}
                    </label>
                    <Input
                      type="text"
                      value={emergencyCode.code}
                      onChange={(e) => setEmergencyCode({...emergencyCode, code: e.target.value.toUpperCase()})}
                      className="cyber-input w-full font-mono tracking-widest uppercase"
                      minLength={4}
                    />
                  </div>
                  <div className="flex items-end">
                    <button
                      type="button"
                      onClick={() => setEmergencyCode({...emergencyCode, enabled: !emergencyCode.enabled})}
                      className={`p-3 rounded-lg transition-colors ${
                        emergencyCode.enabled 
                          ? 'bg-neon-green/20 text-neon-green' 
                          : 'bg-red-500/20 text-red-500'
                      }`}
                    >
                      {emergencyCode.enabled ? <ToggleRight className="w-6 h-6" /> : <ToggleLeft className="w-6 h-6" />}
                    </button>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className={`font-mono text-xs ${emergencyCode.enabled ? 'text-neon-green' : 'text-red-500'}`}>
                    {emergencyCode.enabled 
                      ? (language === 'it' ? '● Attivo' : '● Active')
                      : (language === 'it' ? '○ Disattivato' : '○ Disabled')
                    }
                  </span>
                  <button
                    type="submit"
                    disabled={savingEmergency}
                    className="cyber-btn px-4 py-2 flex items-center gap-2 rounded-md"
                  >
                    {savingEmergency ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    {language === 'it' ? 'Salva' : 'Save'}
                  </button>
                </div>
              </form>
            </div>

            {/* Active Codes List */}
            <div className="cyber-card rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-orbitron text-lg text-white">
                  {language === 'it' ? 'Codici Generati' : 'Generated Codes'}
                </h3>
                <button
                  onClick={fetchAccessCodes}
                  className="text-gray-400 hover:text-white"
                >
                  <RefreshCw className={`w-4 h-4 ${loadingCodes ? 'animate-spin' : ''}`} />
                </button>
              </div>
              
              {loadingCodes ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-neon-green" />
                </div>
              ) : accessCodes.length === 0 ? (
                <p className="font-mono text-sm text-gray-500 text-center py-8">
                  {language === 'it' ? 'Nessun codice generato' : 'No codes generated'}
                </p>
              ) : (
                <div className="space-y-2 max-h-[300px] overflow-y-auto">
                  {accessCodes.map((code) => (
                    <div 
                      key={code.id}
                      className={`flex items-center justify-between p-3 rounded-lg border ${
                        code.used 
                          ? 'bg-gray-500/10 border-gray-500/30' 
                          : code.expired 
                            ? 'bg-red-500/10 border-red-500/30'
                            : 'bg-white/5 border-white/10'
                      }`}
                    >
                      <div className="flex items-center gap-4">
                        <span className={`font-mono text-lg tracking-widest ${
                          code.used ? 'text-gray-500 line-through' : code.expired ? 'text-red-400' : 'text-white'
                        }`}>
                          {code.code}
                        </span>
                        <div className="font-mono text-xs text-gray-500">
                          {code.code_type === 'email' && <Mail className="w-3 h-3 inline mr-1" />}
                          {code.code_type === 'manual' && <Key className="w-3 h-3 inline mr-1" />}
                          {code.client_name || code.email || '-'}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {code.used && (
                          <span className="font-mono text-xs text-gray-500">
                            {language === 'it' ? 'Usato' : 'Used'}
                          </span>
                        )}
                        {code.expired && !code.used && (
                          <span className="font-mono text-xs text-red-400">
                            {language === 'it' ? 'Scaduto' : 'Expired'}
                          </span>
                        )}
                        {!code.used && (
                          <button
                            onClick={() => copyToClipboard(code.code)}
                            className="p-1.5 rounded text-gray-400 hover:text-white hover:bg-white/10"
                          >
                            <Copy className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          onClick={() => handleDeleteCode(code.id)}
                          className="p-1.5 rounded text-gray-400 hover:text-red-400 hover:bg-red-500/10"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </main>

      {/* Details Dialog */}
      <Dialog open={detailsOpen} onOpenChange={closeDetails}>
        <DialogContent className="bg-cyber-gray border-white/10 max-w-2xl max-h-[80vh] overflow-y-auto" onPointerDownOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="font-orbitron text-xl text-white">
              {t('viewDetails')}
            </DialogTitle>
          </DialogHeader>
          
          {selectedSubmission && (
            <div className="space-y-6 mt-4">
              <div>
                <div className="font-mono text-xs text-neon-green/70 mb-1">{t('type')}</div>
                <div className="text-white">{selectedSubmission.investigation_type_name}</div>
              </div>

              <div>
                <div className="font-mono text-xs text-neon-green/70 mb-2">{t('answers')}</div>
                <div className="space-y-3">
                  {Object.entries(selectedSubmission.answers || {}).map(([q, a], i) => (
                    <div key={i} className="bg-cyber-surface/50 rounded p-3">
                      <div className="font-mono text-xs text-neon-cyan mb-1">Q{i + 1}</div>
                      <div className="text-gray-300 text-sm mb-2">{q}</div>
                      <div className="text-white">{a || '-'}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex items-center gap-4">
                <button
                  onClick={() => handleDownloadPdf(selectedSubmission.id)}
                  className="cyber-btn px-4 py-2 flex items-center gap-2"
                >
                  <Download className="w-4 h-4" />
                  {t('downloadPdf')}
                </button>
                <button
                  onClick={() => handleUploadDropbox(selectedSubmission.id)}
                  className="cyber-btn-outline px-4 py-2 flex items-center gap-2 border-blue-500 text-blue-500 hover:bg-blue-500/10"
                >
                  <Cloud className="w-4 h-4" />
                  {language === 'it' ? 'Carica Dropbox' : 'Upload Dropbox'}
                </button>
              </div>
              
              {selectedSubmission.dropbox_path && (
                <div className="mt-2 p-2 bg-blue-500/10 rounded text-xs text-blue-400 font-mono">
                  <Cloud className="w-3 h-3 inline mr-1" />
                  {language === 'it' ? 'Salvato su Dropbox:' : 'Saved to Dropbox:'} {selectedSubmission.dropbox_path}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-cyber-gray border-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-orbitron text-white">
              {language === 'it' ? 'Conferma Eliminazione' : 'Confirm Deletion'}
            </AlertDialogTitle>
            <AlertDialogDescription className="font-mono text-gray-400">
              {t('deleteConfirm')}
              {submissionToDelete && (
                <div className="mt-2 p-2 bg-cyber-surface rounded text-sm">
                  <span className="text-neon-cyan">ID:</span> {submissionToDelete.id.slice(0, 8)}...
                  <br />
                  <span className="text-neon-cyan">{t('type')}:</span> {submissionToDelete.investigation_type_name}
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="cyber-btn-outline">
              {t('cancel')}
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteConfirm}
              className="bg-neon-red hover:bg-neon-red/80 text-white"
            >
              {t('delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
